import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import HomeView from "./view";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "../../redux/reducers/Models";
import { debtsPulling, requestDebts, setUser, clearUser, authorize } from "../../redux/ducks/user";
import { clearCustomer, setCustomer } from "../../redux/ducks/customer";
import { PropsView } from "./Models";
import { Debts, getFormattedDebts } from "@/utils/transform";
import { getIsValidString } from "@/utils/functions";
import useRedirect from "../../hooks/useRedirect";
import { useNavigate, useLocation } from "react-router-dom";
import { useSingleFlag } from "@/hooks/useFlags";
import useCaptcha from "@/hooks/useCaptcha";
import useCaptchaEnterprise from "@/hooks/useCaptchaEnterprise";
import { clearThemeFromStorage } from "@/contexts/ThemeContext";
import { useIsWebView } from "@/hooks/useIsWebView";

export default function Home() {
  useRedirect("home");

  const navigate = useNavigate();
  const location = useLocation();
  const skipBackdrops = !!(location.state as any)?.skipBackdrops;
  const timeoutRef = useRef(2000);
  const activePollingIdRef = useRef<string | null>(null);
  const hasMountedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const dispatch = useDispatch();
  const isWebView = useIsWebView();
  const user = useSelector((state: ReducersTypes & {
    userReducer: ReducersTypes['userReducer'] & {
      cart: PropsView['selectables'];
      debts: PropsView['items'];
    }
  }) => state.userReducer);

  const { isEnabled: isCaptchaMtcCaptcha, isLoading: isLoadingCaptchaFlag } = useSingleFlag('PD_CAPTCHA_MTCAPTCHA');
  const [isLoading, setIsLoading] = useState(false);
  isLoadingRef.current = isLoading;
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  
  const captchaName = useMemo(() => `mtcaptcha-home-modal-${captchaKey}`, [captchaKey]);
  const recaptchaName = useMemo(() => `home-modal-captcha-${captchaKey}`, [captchaKey]);

  const setIsCaptchaVerifiedCallback = useCallback((value: boolean) => {
    setIsCaptchaVerified(value);
  }, []);

  useCaptcha({
    setIsVerified: isCaptchaMtcCaptcha ? setIsCaptchaVerifiedCallback : () => {},
    captchaName
  });

  useCaptchaEnterprise({
    setIsVerified: !isCaptchaMtcCaptcha ? setIsCaptchaVerifiedCallback : () => {},
    captchaName: recaptchaName,
    action: "search"
  });
  
  const goBack = useCallback(() => navigate(-1), [navigate]);
  const goGetout = useCallback(() => {
    clearThemeFromStorage();
    
    dispatch(clearCustomer());
    dispatch(clearUser());

    
    // Tentar sair da webview se estiver em uma
    try {
      // React Native WebView
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({ type: 'close' }));
        return;
      }
      
      if ((window as any).webkit?.messageHandlers?.close) {
        (window as any).webkit.messageHandlers.close.postMessage({});
        return;
      }
      
      if ((window as any).Android?.finish && typeof (window as any).Android.finish === 'function') {
        (window as any).Android.finish();
        return;
      }
      
      // Fallback: tentar window.close() se a janela foi aberta por script
      if (window.opener) {
        window.close();
        return;
      }
    } catch (error) {
      console.warn('Erro ao tentar fechar webview:', error);
    }
    
    navigate('/');
  }, [dispatch, navigate]);
  const goToVehicles = useCallback(() => navigate('/vehicles'), [navigate]);
  const goToProfile = useCallback(() => navigate("/profile"), [navigate]);

  const resetCaptcha = useCallback(() => {
    setIsCaptchaVerified(false);
    if (isCaptchaMtcCaptcha) {
      if ((window as any)?.mtcaptcha?.resetUI) {
        (window as any).mtcaptcha.resetUI(captchaName);
      }
    } else {
      if ((window as any).grecaptcha?.enterprise?.reset) {
        (window as any).grecaptcha.enterprise.reset();
      } else if ((window as any).grecaptcha?.reset) {
        (window as any).grecaptcha.reset();
      }
      (window as any).recaptchaToken = null;
    }
  }, [isCaptchaMtcCaptcha, captchaName]);

  const incrementCaptchaKey = useCallback(() => {
    setCaptchaKey(prev => prev + 1);
  }, []);

  const startPulling = useCallback((reqId: string) => {
    if (!getIsValidString(reqId)) {
      return;
    }

    activePollingIdRef.current = reqId;
    setIsLoading(true);

    const handleDebtsPullingCallback = (success: boolean, code?: string | Debts[]) => {
      if (activePollingIdRef.current !== reqId) return;

      if (!success) {
        if (code === "again") {
          const currentTimeout = timeoutRef.current;
          timeoutRef.current = Math.min(currentTimeout * 2, 10000);
          
          setTimeout(() => {
            if (activePollingIdRef.current !== reqId) return;
            startPulling(reqId);
          }, currentTimeout);
          return;
        }
        setIsLoading(false);
        dispatch(
          setUser({
            debts: [],
            cart: undefined,
          })
        );
        timeoutRef.current = 2000;
        return;
      }

      const formattedDebts = getFormattedDebts(code as Debts[] || []);
      
      dispatch(
        setUser({
          debts: formattedDebts,
          cart: formattedDebts.length > 0 
            ? formattedDebts.map(item => ({ name: item.id, checked: true }))
            : null,
        })
      );
      setIsLoading(false);
      timeoutRef.current = 2000;
    };

    dispatch(
      debtsPulling({
        requestId: reqId,
        callback: handleDebtsPullingCallback,
      })
    );
  }, [dispatch]);


  const onSearchNewPlate = useCallback(async (newPlate: string) => {
    const cleanPlate = newPlate.toUpperCase().replace(/[^A-Z0-9]/g, "");
    
    if (!cleanPlate || cleanPlate.length < 5) {
      return;
    }

    activePollingIdRef.current = null;
    timeoutRef.current = 2000;

    dispatch(
      setUser({
        plate: cleanPlate,
        requestId: undefined,
        debts: [],
        cart: undefined,
      })
    );

    dispatch(
      setCustomer({
        plate: cleanPlate,
      })
    );
    
    setIsLoading(true);

    try {
      let captchaToken = '';
      let recaptchaAction: string | undefined;

      if (isCaptchaMtcCaptcha) {
        const mtcaptchaToken = (window as any)?.mtcaptcha?.getVerifiedToken?.(captchaName);
        const mtcaptchaStatus = (window as any)?.mtcaptcha?.getStatus?.(captchaName);
        
        if (mtcaptchaToken && mtcaptchaStatus?.isVerified) {
          captchaToken = mtcaptchaToken;
        } else {
          if ((window as any)?.mtcaptcha?.getVerifiedToken) {
            const anyToken = (window as any).mtcaptcha.getVerifiedToken(captchaName);
            if (anyToken) {
              captchaToken = anyToken;
            }
          }
          
          if (!captchaToken) {
            captchaToken = (window as any)?.recaptchaToken || '';
          }
        }
      } else {
        let recaptchaToken = (window as any)?.recaptchaToken;
        
        if (!recaptchaToken && (window as any).executeRecaptcha) {
          (window as any).executeRecaptcha();
          await new Promise(resolve => setTimeout(resolve, 300));
          recaptchaToken = (window as any)?.recaptchaToken;
        }
        
        if (recaptchaToken) {
          captchaToken = recaptchaToken;
        }
        
        recaptchaAction = (window as any).getRecaptchaAction ? (window as any).getRecaptchaAction() : undefined;
      }

      if (!captchaToken) {
        await new Promise(resolve => setTimeout(resolve, 200));
        
        if (isCaptchaMtcCaptcha) {
          captchaToken = (window as any)?.mtcaptcha?.getVerifiedToken?.(captchaName) || '';
        } else {
          captchaToken = (window as any)?.recaptchaToken || '';
        }
      }

      dispatch(
        authorize({
          plate: cleanPlate,
          token: captchaToken,
          callback: (success: boolean) => {
            if (!success) {
              setIsLoading(false);
              dispatch(
                setUser({
                  debts: [],
                  cart: undefined,
                })
              );
              return;
            }

            setTimeout(() => {
              dispatch(
                requestDebts({
                  plate: cleanPlate,
                  callback: (success: boolean, code?: string) => {
                  if (!success) {
                    setIsLoading(false);
                    dispatch(
                      setUser({
                        debts: [],
                        cart: undefined,
                      })
                    );
                    return;
                  }

                  if (getIsValidString(code as string)) {
                    dispatch(
                      setUser({
                        requestId: code,
                      })
                    );
                  }
                },
              })
            );
            }, 100);
          },
          recaptchaAction,
        })
      );
    } catch (error) {
      setIsLoading(false);
    }
  }, [dispatch, isCaptchaMtcCaptcha, captchaName]);

  const onChange: PropsView['onChange'] = useCallback((e) => {
    const { name, checked } = e.target;

    if (name === "all") {
      dispatch(
        setUser({
          cart: checked ? user?.debts?.map(item => ({ name: item.id, checked: true })) : null,
        })
      );
      return;
    }

    const updatedSelectables = user?.cart?.filter(item => item.name !== name) || [];

    if (checked) {
      updatedSelectables.push({ name, checked });
    }

    dispatch(
      setUser({
        cart: updatedSelectables.length ? updatedSelectables : null
      })
    );
  }, [dispatch, user?.debts, user?.cart]);

  const handleRequestDebts = useCallback((success: boolean, code?: string) => {
    if (!success) {
      setIsLoading(false);
      dispatch(
        setUser({
          debts: [],
        })
      );
      return;
    }

    dispatch(
      setUser({
        requestId: code,
      })
    );
  }, [dispatch]);

  const getDebts = useCallback(() => {
    if (isLoading) return;
    if (getIsValidString(user?.requestId as string)) return;

    setIsLoading(true);
    dispatch(
      requestDebts({
        plate: user?.plate as string,
        callback: handleRequestDebts,
      })
    );
  }, [isLoading, user?.requestId, user?.plate, dispatch, handleRequestDebts]);

  useEffect(() => {
    if (getIsValidString(user?.requestId as string) && isLoadingRef.current) {
      startPulling(user?.requestId as string);
    }
  }, [user?.requestId, startPulling]);

  useEffect(() => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;

    const shouldGetDebts = !getIsValidString(user?.requestId as string);
    if (shouldGetDebts) {
      getDebts();
    } else if (!isLoadingRef.current) {
      startPulling(user?.requestId as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
 
  const debts = user?.debts || [];
  /* Fluxo avulso: sem company (não veio de URL siga-facil/zapay) e sem dados pessoais ainda */
  const isAvulsoFlow = !user?.company;
  const [showAvulsoBackdrop, setShowAvulsoBackdrop] = useState(false);
  const [showNoDataBackdrop, setShowNoDataBackdrop] = useState(false);
  const [showTagBackdrop, setShowTagBackdrop] = useState(false);
  const [showBeforeLeaveBackdrop, setShowBeforeLeaveBackdrop] = useState(false);
  const avulsoHandledRef = useRef(false);
  const noDataHandledRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    if (skipBackdrops) {
      avulsoHandledRef.current = true;
      return;
    }
    if (!initialLoadDoneRef.current) {
      if (!isLoading && hasMountedRef.current) {
        initialLoadDoneRef.current = true;
      } else {
        return;
      }
    }
    if (avulsoHandledRef.current) return;
    if (!isLoading && debts.length > 0 && isAvulsoFlow) {
      avulsoHandledRef.current = true;
      setShowAvulsoBackdrop(true);
    }
  }, [isLoading, debts.length, isAvulsoFlow, skipBackdrops]);

  useEffect(() => {
    if (skipBackdrops) {
      noDataHandledRef.current = true;
      return;
    }
    if (!initialLoadDoneRef.current) return;
    if (noDataHandledRef.current) return;
    if (!isLoading && debts.length === 0 && isAvulsoFlow) {
      noDataHandledRef.current = true;
      setShowNoDataBackdrop(true);
    }
  }, [isLoading, debts.length, isAvulsoFlow, skipBackdrops]);

  const avulsoTotalValue = debts
    .reduce((sum, item) => sum + (item.original?.vlTotalPagar || 0), 0)
    .toFixed(2)
    .replace(".", ",");
  const goToRegisterAvulso = useCallback(() => {
    setShowAvulsoBackdrop(false);
    setShowBeforeLeaveBackdrop(false);
    setShowNoDataBackdrop(false);
    setShowTagBackdrop(false);
    setIsLoading(false);
    navigate('/register/choosepin');
  }, [navigate]);
  const handleNoDataRegister = useCallback(() => {
    setShowNoDataBackdrop(false);
    navigate('/register');
  }, [navigate]);
  const handleNoDataDismiss = useCallback(() => {
    setShowNoDataBackdrop(false);
    setShowTagBackdrop(true);
  }, []);
  const handleTagContract = useCallback(() => {
    setShowTagBackdrop(false);
    window.open('https://www.semparar.com.br', '_blank');
  }, []);
  const handleTagDismiss = useCallback(() => {
    setShowTagBackdrop(false);
  }, []);
  const handleAvulsoDeny = useCallback(() => {
    setShowAvulsoBackdrop(false);
    setShowBeforeLeaveBackdrop(true);
  }, []);
  const handleBeforeLeaveDismiss = useCallback(() => {
    setShowBeforeLeaveBackdrop(false);
    goGetout();
  }, [goGetout]);

  return (
    <HomeView
      goToVehicles={goToVehicles}
      items={debts}
      plate={user?.plate || ""}
      isLoading={isLoading}
      onChange={onChange}
      selectables={user?.cart || null}
      goBack={goBack}
      goToProfile={goToProfile}
      goGetout={goGetout}
      onSearchNewPlate={onSearchNewPlate}
      isCaptchaVerified={isCaptchaVerified}
      isCaptchaMtcCaptcha={isCaptchaMtcCaptcha}
      captchaName={captchaName}
      resetCaptcha={resetCaptcha}
      incrementCaptchaKey={incrementCaptchaKey}
      isLoadingCaptchaFlag={isLoadingCaptchaFlag}
      showAvulsoBackdrop={showAvulsoBackdrop}
      avulsoPassageCount={debts.length}
      avulsoTotalValue={avulsoTotalValue}
      onAvulsoConfirm={goToRegisterAvulso}
      onAvulsoDeny={handleAvulsoDeny}
      isWebView={isWebView}
      showNoDataBackdrop={showNoDataBackdrop}
      onNoDataRegister={handleNoDataRegister}
      onNoDataDismiss={handleNoDataDismiss}
      showTagBackdrop={showTagBackdrop}
      onTagContract={handleTagContract}
      onTagDismiss={handleTagDismiss}
      showBeforeLeaveBackdrop={showBeforeLeaveBackdrop}
      onBeforeLeavePayNow={goToRegisterAvulso}
      onBeforeLeaveDismiss={handleBeforeLeaveDismiss}
    />
  );
}
